import { resolveViewerId } from 'Pods/Shared/Hooks/useViewerId'

import { DEFAULT_GENERATION_STATUS } from './constants'
import { generationStatusStore } from './index'

// Fields the global status store mirrors from the /ai studio's generation slice.
// To expose a new live-status field globally, add it here - this is the single
// place the mirror reads, so writers never have to remember a separate call.
const MIRRORED_KEYS = [
  'isGenerating',
  'isEnhancing',
  'enhancingSessionId',
  'runId',
  'sessionId',
  'generationStartedAt',
]

/**
 * Mirrors a generation-slice patch into the global status store. Called from the
 * studio store's single setGenerationState choke point, so every live-status change
 * is reflected without per-feature wiring.
 * @param {object} patch - The partial generation-slice update being applied.
 */
export const publishGenerationStatus = (patch) => {
  const mirrored = {}
  MIRRORED_KEYS.forEach((key) => {
    if (key in patch) {
      mirrored[key] = patch[key]
    }
  })

  // A run starting clears the previous outcome so consumers stop surfacing it, and
  // stamps the owner so the global nav only reveals live status to the user who started it.
  // Only stamp when identity has resolved: this same action runs from the off-/ai watcher
  // (useGenerationStatusWatcher) on tabs where the viewer id may not be ready yet, and
  // writing null there would clobber the real owner across every tab via the storage sync.
  if (patch.isGenerating === true) {
    mirrored.lastResult = null
    const viewerId = resolveViewerId()
    if (viewerId != null) {
      mirrored.userId = viewerId
    }
  }

  if (Object.keys(mirrored).length === 0) {
    return
  }

  generationStatusStore.setState(mirrored)
}

/**
 * Records the outcome of a finished run. The live runId is cleared on completion, so
 * the finished run's identity/info lives here (set from the completion broadcast).
 * @param {object} lastResult - The generated/failed run info (see DEFAULT shape).
 */
export const setGenerationResult = (lastResult) => {
  const viewerId = resolveViewerId()

  // Re-stamp the owner on completion, but never blank a resolved owner with null (the
  // watcher can settle a run on a tab whose viewer id has not loaded yet).
  generationStatusStore.setState(viewerId != null ? { lastResult, userId: viewerId } : { lastResult })
}

// Clears the finished-run outcome (e.g. a consumer dismissing the "model ready" state)
// without touching live status.
export const clearGenerationResult = () => {
  generationStatusStore.setState({ lastResult: null })
}

export const resetGenerationStatus = () => {
  generationStatusStore.setState({ ...DEFAULT_GENERATION_STATUS })
}
